import {Box, Group} from '@mantine/core';
import {IconCheck} from '@tabler/icons-react';
import * as React from 'react';
import {Button} from 'src/design/atoms/Button';
import {Subtext} from 'src/design/atoms/Typography';

/**
 * Vertical wizard with per-step Continue/Back controls, disabling Continue
 * while a step reports itself incomplete.
 * @registry Vertical stepper wizard with per-step Continue/Back controls.
 *
 * Ported from `library/elements/VerticalLinearStepper`. Custom rebuild — not
 * built on Mantine `Stepper`, which renders the active step's content as a
 * single panel below the whole step list and can't nest it inline under the
 * active step's label the way MUI's vertical `Stepper` does. This composes
 * plain Mantine primitives (`Box`/`Group`) to replicate that MUI layout:
 * small filled circular badges, a connector line between steps, and the
 * active step's content/controls nested directly under its label with the
 * connector doubling as the content's left border. Prop surface (`steps`,
 * `onCompletedSteps`, `IStepItem`) kept identical.
 */
export interface IStepItem {
  label: string;
  component?: React.ReactNode;
  handleIsContinueDisabled?: () => boolean;
}
export interface VerticalLinearStepperProps {
  steps: IStepItem[];
  onCompletedSteps?: () => void;
}

const BADGE_SIZE = 24;
const CONNECTOR_COLOR = 'var(--mantine-color-gray-4)';

export const VerticalLinearStepper: React.FC<VerticalLinearStepperProps> = ({
  steps,
  onCompletedSteps,
}) => {
  const [activeStep, setActiveStep] = React.useState(0);

  const handleNext = () => {
    if (steps.length - 1 === activeStep) {
      onCompletedSteps?.();
    }
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const isStepDisabled = (step: IStepItem): boolean => {
    if (!step.handleIsContinueDisabled) {
      return false;
    }

    return step.handleIsContinueDisabled();
  };

  return (
    <Box>
      {steps.map((step, index) => {
        const isActive = index === activeStep;
        const isCompleted = index < activeStep;
        const isLastStep = index === steps.length - 1;
        const showConnector = !isActive && !isLastStep;

        return (
          <React.Fragment key={step.label}>
            <Group gap={8} wrap="nowrap" align="flex-start">
              <Box
                style={{
                  width: BADGE_SIZE,
                  height: BADGE_SIZE,
                  borderRadius: '50%',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  color: 'white',
                  backgroundColor:
                    isActive || isCompleted
                      ? 'var(--mantine-color-dark-6)'
                      : 'var(--mantine-color-gray-5)',
                }}>
                {isCompleted ? (
                  <IconCheck size={16} color="white" />
                ) : (
                  index + 1
                )}
              </Box>

              <Box>
                <Box
                  style={{
                    fontSize: 14,
                    lineHeight: `${BADGE_SIZE}px`,
                    color:
                      isActive || isCompleted
                        ? 'var(--mantine-color-text)'
                        : 'var(--mantine-color-dimmed)',
                  }}>
                  {step.label}
                </Box>
                {isLastStep && <Subtext text="Last step" size="xs" />}
              </Box>
            </Group>

            {isActive && (
              <Box
                style={{
                  marginLeft: BADGE_SIZE / 2,
                  borderLeft: `1px solid ${CONNECTOR_COLOR}`,
                  paddingLeft: 20,
                  paddingBottom: 8,
                }}>
                {step.component}

                <Box mb="xs">
                  {index !== 0 && (
                    <Button
                      onClick={handleBack}
                      variant="light"
                      color="black"
                      size="sm"
                      mt="sm"
                      mr="sm">
                      Back
                    </Button>
                  )}
                  <Button
                    onClick={handleNext}
                    mt="sm"
                    mr="sm"
                    size="sm"
                    disabled={isStepDisabled(step)}>
                    {isLastStep ? 'Finish' : 'Continue'}
                  </Button>
                </Box>
              </Box>
            )}

            {showConnector && (
              <Box
                style={{
                  marginLeft: BADGE_SIZE / 2,
                  borderLeft: `1px solid ${CONNECTOR_COLOR}`,
                  minHeight: BADGE_SIZE,
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};
